import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import Button from "@mui/material/Button";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import { casesActions } from "@_actions/cases.actions";
import { caseActions } from "@_actions/case.actions";
import { stateError, validateDomain } from "@_utils/helpers";
import styles from "./AddCasePopup.module.css";
import { sidebarActions } from "@_actions/sidebar.actions";
import CircularProgress from "@mui/material/CircularProgress";
import { caseConstants } from "@_constants/case.constants";

const defaultState = {
  host: "",
  source_case_id: "",
};

function AddCasePopup({ close, open }) {
  const casesReducer = useSelector((state) => state.casesReducer);
  const history = useHistory();
  const dispatch = useDispatch();

  const [inputs, setInputs] = useState(defaultState);
  const [isClicked, setIsClicked] = useState(false);

  const isSubmitting = casesReducer.status.CASES_ADD === "request";

  const closePopup = useCallback(() => {
    setInputs(defaultState);
    setIsClicked(false);
    close();
  }, [close]);

  function actionClicked() {
    setIsClicked(true);
    if (inputs.host.trim() === "" || !validateDomain(inputs.host.trim())) {
      alert("Please use a valid domain name e.g. domain.com");
      return;
    }
    dispatch(casesActions.addCases(inputs));
  }

  // Single effect: react to CASES_ADD success.
  // Reducer holds the full new_case object in `message`.
  useEffect(() => {
    if (casesReducer.status.CASES_ADD !== "success") return;
    const newCase = casesReducer.message;
    if (newCase?.id) {
      dispatch(sidebarActions.updateStatus(caseConstants.CASE_STATE_STANDBY));
      dispatch(caseActions.getCaseWithChargeDetails(newCase.id));
      dispatch(casesActions.getCasesCount());
      history.push(`/cases/${newCase.case_id}`);
    }
    dispatch(casesActions.clearStateMessage("CASES_ADD"));
    closePopup();
    // Only re-run when the CASES_ADD status transitions (e.g. request → success).
    // Other deps (dispatch, history, closePopup) are stable and intentionally
    // omitted to avoid re-firing the effect on unrelated renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casesReducer.status.CASES_ADD]);

  function handleInputChange(e) {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  }

  return (
    <DialogPopup title="Add New Case" open={open} onClose={closePopup}>
      <div className={styles.content_container}>
        <TextfieldInput
          label="Domain"
          id={"host"}
          name={"host"}
          placeholder={"e.g. domain.com"}
          value={inputs.host}
          onChange={handleInputChange}
          error={isClicked && (!inputs.host || inputs.host === "")}
          required
        />
        <TextfieldInput
          label="Case Reference Number"
          id={"source_case_id"}
          name={"source_case_id"}
          value={inputs.source_case_id}
          onChange={handleInputChange}
        />
      </div>
      {stateError(casesReducer.status.CASES_ADD) && (
        <div className={styles.error_container}>
          <div className={styles.error_text}>
            {stateError(casesReducer.status.CASES_ADD)}
          </div>
        </div>
      )}
      <div className="action_container">
        {isSubmitting ? (
          <CircularProgress />
        ) : (
          <>
            <Button className="btn_cancel" onClick={closePopup}>
              Cancel
            </Button>
            <Button className="btn_confirm" onClick={actionClicked}>
              Confirm
            </Button>
          </>
        )}
      </div>
    </DialogPopup>
  );
}

export default AddCasePopup;
